import { computed, toValue, type MaybeRefOrGetter } from 'vue'
import type { Resource } from '@/resources/resource_types'
import type { AnchorObjective } from '../objectives/anchor_objective_types'
import { useGoalResourcesStore } from '../goal_resources.store'

/** The accordion objectives list's per-objective resources, loading flags and selected languages, from the resources store. */
export function useObjectiveResourceMaps(objectives: MaybeRefOrGetter<AnchorObjective[]>) {
  const goalResourcesStore = useGoalResourcesStore()

  const objectiveIds = computed(() =>
    toValue(objectives).map((objective) => objective.uuid).filter((uuid): uuid is string => Boolean(uuid))
  )

  function byObjectiveId<T>(valueFor: (objectiveId: string) => T): Record<string, T> {
    return Object.fromEntries(objectiveIds.value.map((objectiveId) => [objectiveId, valueFor(objectiveId)]))
  }

  const resourcesByObjectId = computed<Record<string, Resource[]>>(() => byObjectiveId(goalResourcesStore.resourcesById))
  const isFetchingResourcesByObjectId = computed(() => byObjectiveId(goalResourcesStore.isFetchingResourcesById))

  const selectedLanguages = computed({
    get: () => byObjectiveId(goalResourcesStore.selectedLanguageById),
    set: (languagesByObjectId: Record<string, string>) => {
      Object.entries(languagesByObjectId).forEach(([objectiveId, language]) => goalResourcesStore.setSelectedLanguage(objectiveId, language))
    }
  })

  return { resourcesByObjectId, isFetchingResourcesByObjectId, selectedLanguages }
}
